import mitt, { type Emitter } from 'mitt';

export type ToastOptions = {
  dontHide?: boolean;
  [key: string]: unknown;
};

export type ToastPayload = {
  message: string;
  options?: ToastOptions;
};

type Events = {
  'toast-error': ToastPayload;
  'toast-success': ToastPayload;
  'toast-warning': ToastPayload;
  'toast-clear': undefined;
} & Record<string | symbol, unknown>;

const emitter = mitt<Events>();

type ToastEventName = 'toast-error' | 'toast-success' | 'toast-warning';

type AppEmitter = Omit<Emitter<Events>, 'emit'> & {
  emit(type: 'toast-clear'): void;
  emit(type: ToastEventName, message: string, options?: ToastOptions): void;
  emit<Key extends keyof Events>(type: Key, event: Events[Key]): void;
  emit<Key extends keyof Events>(type: undefined extends Events[Key] ? Key : never): void;
};

const emit: AppEmitter['emit'] = (type, messageOrPayload?, options?) => {
  if (type === 'toast-error' || type === 'toast-success' || type === 'toast-warning') {
    emitter.emit(type, {
      message: messageOrPayload as string,
      options: options as ToastOptions | undefined,
    });
    return;
  }

  if (type === 'toast-clear') {
    emitter.emit('toast-clear', undefined);
    return;
  }

  emitter.emit(type as keyof Events, messageOrPayload as Events[keyof Events]);
};

export const $emitter: AppEmitter = {
  on: emitter.on,
  off: emitter.off,
  emit,
};

export default $emitter;
